/* eslint-disable react/no-unescaped-entities */
import { Link } from 'react-router-dom';
import { ChevronRight, ArrowLeft } from 'lucide-react';

/**
 * LibraryPageLayout — shared chrome for every page of the Bibliothèque HÉRITA.
 *
 * Reusable for /comprendre-document-notarie and every future educational page
 * (comprendre-donation-partage, comprendre-testament, etc.). Children render
 * inside a single `<main>`; the layout owns the brand header, breadcrumb
 * navigation, and the cross-linked footer.
 *
 * Props:
 *  - breadcrumb: [{ label, to? }]  — last item is current page (no link).
 *  - testId: data-testid for the root container.
 */
export const LibraryPageLayout = ({ breadcrumb = [], testId, children }) => {
  return (
    <div
      className="min-h-screen bg-[#F9F7F3] text-[#2C363F]"
      data-testid={testId}
    >
      {/* Brand header — identical to the legal and dashboard headers */}
      <header className="border-b border-[#E2DBCF] bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="font-['IBM_Plex_Mono'] text-[11px] sm:text-[12px] uppercase tracking-[0.28em] text-[#1A2530] hover:opacity-80 transition-opacity"
            data-testid="library-brand-link"
          >
            HÉRITA
          </Link>
          <Link
            to="/"
            className="inline-flex items-center font-['Work_Sans'] text-[12px] sm:text-[13px] text-[#2C363F] hover:text-[#1A2530] transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5 mr-2" aria-hidden="true" />
            Accueil
          </Link>
        </div>
      </header>

      {/* Breadcrumb */}
      {breadcrumb.length > 0 && (
        <nav
          aria-label="Fil d'Ariane"
          className="border-b border-[#E2DBCF]/60 bg-white"
        >
          <ol className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-3 flex flex-wrap items-center gap-1.5 font-['Work_Sans'] text-[12px] sm:text-[12.5px] text-[#2C363F]">
            {breadcrumb.map((item, i) => {
              const isLast = i === breadcrumb.length - 1;
              return (
                <li
                  key={`${item.label}-${i}`}
                  className="inline-flex items-center gap-1.5"
                >
                  {item.to && !isLast ? (
                    <Link
                      to={item.to}
                      className="hover:text-[#1A2530] transition-colors"
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <span
                      aria-current={isLast ? 'page' : undefined}
                      className={isLast ? 'text-[#1A2530] font-medium' : ''}
                    >
                      {item.label}
                    </span>
                  )}
                  {!isLast && (
                    <ChevronRight
                      className="w-3 h-3 text-[#D3CCBE]"
                      aria-hidden="true"
                    />
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      )}

      <main id="main">{children}</main>

      {/* Footer — matches HomePage / LegalPageLayout */}
      <footer className="border-t border-[#E2DBCF] bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-10">
          <p className="font-['Work_Sans'] text-[12px] sm:text-[13px] text-[#2C363F] italic text-center mb-6 leading-relaxed opacity-90 max-w-2xl mx-auto">
            Hérita fournit une lecture informative et ne remplace pas le conseil
            de votre notaire.
          </p>
          <div className="flex flex-col md:flex-row justify-between items-center pt-6 border-t border-[#E2DBCF]/60 gap-4">
            <p className="font-['Work_Sans'] text-sm text-[#2C363F]">
              © 2026 Hérita. Tous droits réservés.
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-2 justify-center">
              <Link to="/comprendre-document-notarie" className="font-['Work_Sans'] text-sm text-[#2C363F] hover:text-[#1A2530] transition-colors">Guides</Link>
              <Link to="/mentions-legales" className="font-['Work_Sans'] text-sm text-[#2C363F] hover:text-[#1A2530] transition-colors">Mentions légales</Link>
              <Link to="/confidentialite" className="font-['Work_Sans'] text-sm text-[#2C363F] hover:text-[#1A2530] transition-colors">Confidentialité &amp; sécurité</Link>
              <Link to="/cgu" className="font-['Work_Sans'] text-sm text-[#2C363F] hover:text-[#1A2530] transition-colors">CGU</Link>
              <Link to="/contact" className="font-['Work_Sans'] text-sm text-[#2C363F] hover:text-[#1A2530] transition-colors">Contact</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LibraryPageLayout;
